import { Box } from '@mui/material';
import React, { ReactElement } from 'react';
import { useNotificationsApi } from 'services/api/hooks/useNotificationsApi.hook';
import useSWR from 'swr';
import { Drawer, DrawerProps } from '../Drawer';
import { NotificationHeader } from './NotificationHeader';
import { NotificationList } from './NotificationList';
import { useNotificationHandlers } from './hooks';

export type NotificationDrawerProps = Omit<DrawerProps, 'title' | 'children'>;

export const NotificationDrawer = (
  props: NotificationDrawerProps,
): ReactElement => {
  const notificationsApi = useNotificationsApi();

  const { data: notificationsData, mutate: mutateNotifications } = useSWR(
    props.open ? 'notifications' : null,
    async () => {
      const result = await notificationsApi.getNotifications(1, 50);
      if (!result.success) return { notifications: [], count: 0 };
      return result.value;
    },
    {
      revalidateOnFocus: true,
      refreshInterval: 30000, // Refresh every 30 seconds
    },
  );

  const { data: unreadCountData, mutate: mutateUnreadCount } = useSWR(
    props.open ? 'notifications-unread-count' : null,
    async () => {
      const result = await notificationsApi.getUnreadCount();
      if (!result.success) return { count: 0 };
      return result.value;
    },
    {
      revalidateOnFocus: true,
      refreshInterval: 30000,
    },
  );

  const { handleNotificationClick, handleMarkAllAsRead } =
    useNotificationHandlers({
      onNotificationsMutate: mutateNotifications,
      onUnreadCountMutate: mutateUnreadCount,
      onClose: props.onClose,
    });

  const notifications = notificationsData?.notifications || [];
  const unreadCount = unreadCountData?.count || 0;

  return (
    <Drawer width={600} padding={0} title="Notifications" {...props}>
      <Box height="100%" display="flex" flexDirection="column">
        <NotificationHeader
          unreadCount={unreadCount}
          onMarkAllAsRead={handleMarkAllAsRead}
        />
        <NotificationList
          notifications={notifications}
          isLoading={!notificationsData}
          onNotificationClick={handleNotificationClick}
        />
      </Box>
    </Drawer>
  );
};
